export type ReviewCampaignLike = {
  status: string;
  createdAt?: Date | string;
  startDate?: Date | string;
};

export type ReviewPostOwnerLike =
  | { type: "campaign"; id: string; name: string }
  | { type: "standalone"; id: string; name: string };

export type ReviewPostLike = {
  scheduledFor: string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  status: string;
  approvalStatus: string | null;
  owner: ReviewPostOwnerLike;
  output?: unknown;
};

export type ReviewPostGroup<TPost extends ReviewPostLike> = {
  key: string;
  type: "campaign" | "standalone";
  id: string | null;
  title: string;
  description: string;
  posts: TPost[];
};

export type ReviewPostGroupLabels = {
  standaloneTitle: string;
  standaloneDescription: string;
  campaignDescription: string;
};

const defaultReviewPostGroupLabels: ReviewPostGroupLabels = {
  standaloneTitle: "One-off posts",
  standaloneDescription: "Posts created outside campaigns.",
  campaignDescription: "Generated assets from this campaign.",
};

const defaultReviewPostStatuses = new Set(["generated"]);
const activeGeneratingPostStatuses = new Set(["scheduled", "generating"]);
const activeGenerationAttemptStatuses = new Set(["queued", "processing"]);

const getDateTime = (value: Date | string | undefined): number => {
  if (!value) {
    return Number.NEGATIVE_INFINITY;
  }

  const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
};

const getReviewPostSortTime = (post: ReviewPostLike): number =>
  getDateTime(post.updatedAt ?? post.createdAt ?? post.scheduledFor);

const getLatestAttemptStatus = (output: unknown): string | null => {
  if (!output || typeof output !== "object" || !("latestAttempt" in output)) {
    return null;
  }

  const latestAttempt = output.latestAttempt;
  if (!latestAttempt || typeof latestAttempt !== "object" || !("status" in latestAttempt)) {
    return null;
  }

  return typeof latestAttempt.status === "string" ? latestAttempt.status : null;
};

export type InstagramReviewConnectionLike =
  | {
      status: string;
      instagramAccountId: string | null;
    }
  | null
  | undefined;

export const getPendingReviewCampaigns = <TCampaign extends ReviewCampaignLike>(
  campaigns: readonly TCampaign[],
): TCampaign[] =>
  campaigns
    .filter((campaign) => campaign.status === "pending_review")
    .sort(
      (a, b) => getDateTime(b.createdAt ?? b.startDate) - getDateTime(a.createdAt ?? a.startDate),
    );

export const groupReviewPostsByOwner = <TPost extends ReviewPostLike>(
  posts: readonly TPost[],
  labels: ReviewPostGroupLabels = defaultReviewPostGroupLabels,
  statuses: ReadonlySet<string> = defaultReviewPostStatuses,
  options: { includeApproved?: boolean } = {},
): ReviewPostGroup<TPost>[] => {
  const groups: ReviewPostGroup<TPost>[] = [];
  const groupMap = new Map<string, ReviewPostGroup<TPost>>();

  for (const post of posts) {
    if (
      !statuses.has(post.status) ||
      (!options.includeApproved && post.approvalStatus === "approved")
    ) {
      continue;
    }

    const isStandalone = post.owner.type === "standalone";
    const key = isStandalone ? "standalone" : `campaign:${post.owner.id}`;
    let group = groupMap.get(key);
    if (!group) {
      group = {
        key,
        type: post.owner.type,
        id: isStandalone ? null : post.owner.id,
        title: isStandalone ? labels.standaloneTitle : post.owner.name,
        description: isStandalone ? labels.standaloneDescription : labels.campaignDescription,
        posts: [],
      };
      groupMap.set(key, group);
      groups.push(group);
    }

    group.posts.push(post);
  }

  for (const group of groups) {
    group.posts.sort((a, b) => getReviewPostSortTime(b) - getReviewPostSortTime(a));
  }

  return groups.toSorted((a, b) => {
    const [aNewestPost] = a.posts;
    const [bNewestPost] = b.posts;
    return (
      (bNewestPost ? getReviewPostSortTime(bNewestPost) : Number.NEGATIVE_INFINITY) -
      (aNewestPost ? getReviewPostSortTime(aNewestPost) : Number.NEGATIVE_INFINITY)
    );
  });
};

export const isActivelyGeneratingReviewPost = (post: ReviewPostLike): boolean =>
  activeGeneratingPostStatuses.has(post.status) &&
  activeGenerationAttemptStatuses.has(getLatestAttemptStatus(post.output) ?? "");

export const formatReviewPostGroupDateRange = (
  posts: readonly ReviewPostLike[],
  formatter: Intl.DateTimeFormat,
): string => {
  const [firstPost] = posts;
  if (!firstPost) {
    return "";
  }

  let earliest = firstPost;
  let latest = firstPost;
  let earliestTime = new Date(firstPost.scheduledFor).getTime();
  let latestTime = earliestTime;
  for (let index = 1; index < posts.length; index += 1) {
    const post = posts[index]!;
    const scheduledTime = new Date(post.scheduledFor).getTime();
    if (scheduledTime < earliestTime) {
      earliest = post;
      earliestTime = scheduledTime;
    }
    if (scheduledTime > latestTime) {
      latest = post;
      latestTime = scheduledTime;
    }
  }

  const earliestLabel = formatter.format(new Date(earliest.scheduledFor));
  const latestLabel = formatter.format(new Date(latest.scheduledFor));

  return earliestLabel === latestLabel ? earliestLabel : `${earliestLabel} - ${latestLabel}`;
};

export const isInstagramPublishingReady = (connection: InstagramReviewConnectionLike): boolean =>
  connection?.status === "connected" && Boolean(connection.instagramAccountId?.trim());

export const shouldShowInstagramPublishingSetupBanner = (
  connection: InstagramReviewConnectionLike,
): boolean => connection !== undefined && !isInstagramPublishingReady(connection);

export const formatReviewBrandEyebrow = (sourceDomain: string): string => {
  const host = sourceDomain
    .trim()
    .toLowerCase()
    .replace(/^www\./, "");
  const label = (host.split(".").filter(Boolean)[0] ?? host).replace(/[-_]+/g, " ").trim();

  return (label || "Brand").toUpperCase();
};
